import { Save, Check, Loader2 } from 'lucide-react';

type Tone = 'teal' | 'violet';

interface SaveButtonProps {
  onClick: () => void;
  saving?: boolean;
  isDirty?: boolean;
  label?: string;
  savedLabel?: string;
  size?: 'sm' | 'md';
  tone?: Tone;
  className?: string;
}

const TONES: Record<Tone, string> = {
  teal: 'bg-teal-600 hover:bg-teal-700',
  violet: 'bg-violet-600 hover:bg-violet-700',
};

/**
 * Global save button with three visual states:
 * - isDirty=false (no unsaved changes): muted, disabled, "Guardado" + check
 * - isDirty=true (unsaved changes): full colour, "Guardar" + save icon
 * - saving=true: spinner, disabled, "Guardando…"
 */
export default function SaveButton({
  onClick,
  saving = false,
  isDirty = false,
  label = 'Guardar',
  savedLabel = 'Guardado',
  size = 'md',
  tone = 'teal',
  className = '',
}: SaveButtonProps) {
  const iconSize = size === 'sm' ? 14 : 16;
  const padding = size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm';
  const solid = TONES[tone];

  if (saving) {
    return (
      <button
        type="button"
        disabled
        className={`flex items-center gap-2 ${padding} ${solid} text-white rounded-lg font-medium opacity-75 cursor-wait ${className}`}
      >
        <Loader2 size={iconSize} className="animate-spin" />
        Guardando…
      </button>
    );
  }

  if (!isDirty) {
    return (
      <button
        type="button"
        disabled
        className={`flex items-center gap-2 ${padding} bg-slate-100 text-slate-400 rounded-lg font-medium cursor-default ${className}`}
      >
        <Check size={iconSize} />
        {savedLabel}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 ${padding} ${solid} text-white rounded-lg font-medium transition-colors ${className}`}
    >
      <Save size={iconSize} />
      {label}
    </button>
  );
}
